/**
 * PipeTestingAuditLogModal - Searchable audit log for Pipe Testing tracker actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

const PipeTestingAuditLogModal = ({ isOpen, onClose, projectId, jobNumber }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const LIMIT = 50;

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: LIMIT, offset: page * LIMIT });
      if (search) params.set('search', search);

      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/pipe-testing-audit-log/project/${projectId}?${params.toString()}`
      );
      if (response.data.success) {
        setEntries(response.data.data.rows || []);
        setTotalRows(response.data.data.totalRows || 0);
      }
    } catch (error) {
      console.error('Error fetching Pipe Testing audit log:', error);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId, search, page]);

  useEffect(() => {
    if (isOpen) fetchEntries();
  }, [isOpen, fetchEntries]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('en-CA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const styles = {
      import: { bg: '#dbeafe', color: '#1e40af', label: 'Import' },
      excel_import: { bg: '#dbeafe', color: '#1e40af', label: 'Excel Import' },
      edit: { bg: '#fef3c7', color: '#92400e', label: 'Edit' },
      add: { bg: '#d1fae5', color: '#065f46', label: 'Add' },
      duplicate: { bg: '#ede9fe', color: '#5b21b6', label: 'Duplicate' },
      delete: { bg: '#fee2e2', color: '#991b1b', label: 'Delete' },
    };
    const s = styles[action] || { bg: '#f3f4f6', color: '#374151', label: action };
    return (
      <span style={{
        padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
        backgroundColor: s.bg, color: s.color, textTransform: 'uppercase',
      }}>
        {s.label}
      </span>
    );
  };

  const formatDetails = (details) => {
    if (!details) return '';
    if (typeof details === 'string') {
      try { details = JSON.parse(details); } catch { return details; }
    }
    return Object.entries(details)
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
      .join(', ');
  };

  const totalPages = Math.ceil(totalRows / LIMIT);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', width: '800px', maxHeight: '80vh',
        overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #0f766e 0%, #0891b2 100%)', color: 'white',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>Pipe Testing Audit Log</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.9 }}>
              {totalRows} total entries
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer',
          }}>&times;</button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search actions, users, details..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px 8px 36px',
                borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px',
              }}
            />
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}>
              🔍
            </span>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', maxHeight: 'calc(80vh - 220px)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
          ) : entries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              {search ? 'No results matching your search.' : 'No audit log entries yet.'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0 }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>User</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Action</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>
                      {formatDate(entry.created_at)}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {entry.user_email?.split('@')[0] || 'system'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {getActionBadge(entry.action)}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#6b7280', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {formatDetails(entry.details)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            padding: '12px 24px', borderTop: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center',
          }}>
            <button
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
              style={{
                padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '4px',
                cursor: page === 0 ? 'default' : 'pointer', fontSize: '13px',
                backgroundColor: page === 0 ? '#f3f4f6' : '#fff',
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Page {page + 1} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
              style={{
                padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '4px',
                cursor: page >= totalPages - 1 ? 'default' : 'pointer', fontSize: '13px',
                backgroundColor: page >= totalPages - 1 ? '#f3f4f6' : '#fff',
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PipeTestingAuditLogModal;
